import { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import i18n from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../Common';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const t = i18n.t.bind(i18n);

// `reviewId` (+ optional `initialSrc`) fetches the signature on demand, once it scrolls into
// view, instead of requiring the caller to resolve a SAS URL for every review up front. Callers
// that already have a `src` (or don't need lazy fetching) are unaffected — `src` always wins.
const SignatureImage = ({
    src, alt, id, style, className, reviewId, initialSrc,
}) => {
    const [fetchedSrc, setFetchedSrc] = useState(initialSrc || null);
    const [loading, setLoading] = useState(false);

    const { ref, isIntersecting } = useIntersectionObserver({ freezeOnceVisible: true });

    const effectiveSrc = src || fetchedSrc;

    useEffect(() => {
        if (effectiveSrc || !reviewId || !isIntersecting) return;

        let cancelled = false;
        setLoading(true);

        httpClient.js(ApiRoutes.timesheetReview.signatureImage(reviewId))
            .then((result) => {
                if (cancelled) return;
                setFetchedSrc(result?.data ? `data:image/png;base64,${result.data}` : null);
            })
            .catch(() => {
                if (!cancelled) setFetchedSrc(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isIntersecting, reviewId, effectiveSrc]);

    if (reviewId && !effectiveSrc && loading) {
        return (
            <div ref={ref}>
                <Spinner size="sm" />
            </div>
        );
    }

    return effectiveSrc
        ? (
            <img
                alt={alt}
                id={id}
                style={style}
                className={className}
                src={effectiveSrc}
            />
        )
        : (
            <span
                ref={reviewId ? ref : undefined}
                id={id}
                className={`text-muted ${className ?? ''}`}
            >
                {t('timesheet:noSignatureAvailable')}
            </span>
        );
};

export default SignatureImage;
